// ─── Impostazioni → Listino: la settimana dei prezzi, e le righe che la fanno ─
//
// 🚨⭐⭐ IL PIANO CHIAMA QUESTA LA REGOLA «più facile da sbagliare e più difficile
// da accorgersene» (§ 06, Fase 3): un prezzo sbagliato non dà errore — esce
// dalla cassa come un numero qualunque, e lo scopre chi quadra la serata.
//
// 🗣️ Riscritta il 17/09/2026 su tre sue richieste dette insieme: *«semplificarmi
// la creazione dei listini»*, *«poter modificare un listino»*, *«ripeterlo per
// alcuni giorni e per alcune fasce orarie in determinati giorni della
// settimana»* — e con l'indicazione di dove guardare: *«come abbiamo fatto
// fasce»*.
//
// ⭐⭐ E la metà che semplifica DAVVERO non è il modulo: è la GRIGLIA. Nelle
// Fasce è la striscia a far vedere i buchi; qui è una settimana di mezz'ore. La
// differenza è che un'ora senza fascia si vede — il calendario la disegna in
// grigio — mentre un'ora senza PREZZO non si vede da nessuna parte.
//
// ⭐ E la griglia non rifà il conto: lo chiede al server, che applica la STESSA
// funzione (`scegliPrezzo`) che mette l'importo addosso a una prenotazione. Una
// mappa disegnata da una seconda regola tranquillizza e sbaglia insieme.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  GIORNI,
  ORDINE_SETTIMANA,
  TIPI_PRENOTAZIONE,
  piuGiorni,
  trovaConflitti,
  type Campo as CampoCircolo,
  type EsitoPrezzo,
  type RigaListino,
  type SettimanaPrezzi,
  type TipoPrenotazione,
} from '@pmo/shared';
import { Link } from 'react-router-dom';
import { chiama } from '../../lib/api.js';
import { inEuro, perGiorni } from '../../lib/esiti.js';
import { CampoData } from '../../components/CampoData.js';
import { CampoOra } from '../../components/CampoOra.js';
import { inItaliano } from '../../lib/time.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';
import { SoloLettura, euro, usaModifica, usaRilettura } from './comune.js';

/**
 * ⭐ UNA chiave sola per l'elenco E per la griglia, di proposito: `invalidate`
 * lavora per prefisso, quindi una riga salvata rilegge tutt'e due. Con due
 * chiavi slegate la griglia resterebbe a mostrare i prezzi di prima — cioè
 * mentirebbe proprio nel momento in cui la si guarda per controllare.
 */
const CHIAVE = 'listino';

/** Il valore del menu «Campo» quando la riga vale per tutti: `courtId` nullo. */
const OGNI_CAMPO = '';

interface Conflitto {
  a: RigaListino;
  b: RigaListino;
  perche: string;
}

export function Listino(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  /**
   * 🗣️ *«ripeterlo per alcuni giorni»* ⇒ la scelta sta QUI e non dentro la
   * riga: si spuntano tre righe della sera e si copiano su lunedì, mercoledì e
   * venerdì in un gesto solo. È la forma che le Fasce hanno già.
   * ⚠️ Sono ID e non indici: dopo una rilettura le posizioni ballano, gli ID no.
   */
  const [selezionate, setSelezionate] = useState<string[]>([]);
  const elenco = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<{ items: RigaListino[]; conflitti: Conflitto[] }>('GET', '/settings/prices'),
  });
  const campiQuery = useQuery({
    queryKey: ['campi'],
    queryFn: () => chiama<{ items: CampoCircolo[] }>('GET', '/settings/courts'),
  });

  if (elenco.isLoading) return <Attesa cosa="il listino" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  const items = elenco.data?.items ?? [];
  const conflitti = elenco.data?.conflitti ?? [];
  const campi = campiQuery.data?.items ?? [];
  // Una riga cancellata da qualcun altro non deve restare «scelta» in un elenco
  // che non la contiene più: la verità è l'elenco, non la spunta.
  const scelte = items.filter((r) => selezionate.includes(r.id));
  /**
   * ⚠️ In tabella i giorni si leggono da LUNEDÌ, non da zero.
   *
   * 📏 Visto guardando la schermata, non rileggendo il codice: il server ordina
   * per numero del giorno e nel database **domenica è lo zero** — quindi la
   * domenica compariva in cima, prima del lunedì, in mezzo a righe altrimenti
   * identiche. Nessun errore, solo un elenco che non si percorre.
   * 📌 Il numero è il DATO e non si tocca; l'ordine di lettura è una cosa che
   *    riguarda chi guarda, e sta qui.
   */
  const inOrdine = [...items].sort(
    (a, b) =>
      a.bookingType.localeCompare(b.bookingType) ||
      a.durationMinutes - b.durationMinutes ||
      ORDINE_SETTIMANA.indexOf((a.weekday ?? -1) as 0) - ORDINE_SETTIMANA.indexOf((b.weekday ?? -1) as 0) ||
      (a.fromTime ?? '').localeCompare(b.fromTime ?? ''),
  );

  return (
    <section>
      <header className="testata">
        <h2>Listino — le righe di prima</h2>
        {/*
          ⚠️ Questa pagina non è più la strada principale, e deve dirlo da sé:
          dal 17/09/2026 il listino è un FOGLIO di caselle (⇒ `Listini`). Queste
          righe rispondono **solo** finché nessun listino copre quel giorno, e
          una pagina che non dichiara di essere un ripiego viene usata come se
          non lo fosse.
        */}
        <p className="tenue">
          {items.length} {items.length === 1 ? 'riga' : 'righe'}. Sono il meccanismo di prima: danno il prezzo{' '}
          <strong>solo nei giorni che nessun listino copre</strong>. Il listino vero si scrive in{' '}
          <Link to="/impostazioni/listino">Listino prezzi giocatori</Link>.
        </p>
        <p className="tenue piccolo">
          La griglia qui sotto dice cosa rispondono queste righe, mezz'ora per mezz'ora,{' '}
          <strong>e dove non rispondono</strong>.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      <Settimana campi={campi} />

      {conflitti.length > 0 && <Conflitti conflitti={conflitti} />}

      {items.length === 0 ? (
        <Vuoto
          titolo="Listino vuoto"
          perché="Senza righe una prenotazione nasce senza prezzo — che è diverso da nascere gratis."
        />
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                {modificabile && <th aria-label="Scegli" />}
                <th>Tipo</th>
                <th>Giorno</th>
                <th>Fascia</th>
                <th>Campo</th>
                <th>Durata</th>
                <th>Periodo</th>
                <th>Prezzo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {inOrdine.map((r) => (
                <Riga
                  key={r.id}
                  riga={r}
                  campi={campi}
                  modificabile={modificabile}
                  scelta={selezionate.includes(r.id)}
                  scegli={(id, dentro) =>
                    setSelezionate((p) => (dentro ? [...p, id] : p.filter((x) => x !== id)))
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modificabile && <Nuova campi={campi} />}

      {modificabile && scelte.length > 0 && (
        <BarraScelte scelte={scelte} tutte={items} campi={campi} svuota={() => setSelezionate([])} />
      )}
    </section>
  );
}

// ── ⭐⭐ La settimana dei prezzi ──────────────────────────────────────────────

/**
 * La griglia, e la prova del prezzo che ne discende.
 *
 * 🔄 Qui c'erano DUE riquadri: «prova il prezzo», con sei caselle da riempire a
 * mano, e nient'altro. ⇒ Per sapere se il listino aveva un buco bisognava
 * **indovinare dove cercarlo**, una domanda per volta.
 * ⭐ Adesso la domanda è rovesciata: la settimana si mostra intera, e la prova
 *   è il DETTAGLIO di una mezz'ora che si clicca. La casella che si riempiva a
 *   mano — giorno e ora — è diventata il gesto di indicare.
 */
function Settimana({ campi }: { campi: CampoCircolo[] }): React.JSX.Element {
  const oggi = new Date().toISOString().slice(0, 10);
  const [tipo, setTipo] = useState<TipoPrenotazione>('partita');
  const [campoId, setCampoId] = useState(OGNI_CAMPO);
  const [durata, setDurata] = useState('90');
  const [giocatori, setGiocatori] = useState('4');
  const [giorno, setGiorno] = useState(oggi);
  /** La mezz'ora indicata: è la domanda del riquadro di dettaglio. */
  const [cella, setCella] = useState<{ date: string; time: string } | null>(null);

  const parametri = new URLSearchParams({
    bookingType: tipo,
    durationMinutes: durata,
    players: giocatori,
  });
  if (campoId !== OGNI_CAMPO) parametri.set('courtId', campoId);

  const settimana = useQuery({
    queryKey: [CHIAVE, 'settimana', tipo, campoId, durata, giocatori, giorno],
    queryFn: () => {
      const q = new URLSearchParams(parametri);
      q.set('date', giorno);
      return chiama<SettimanaPrezzi>('GET', `/settings/prices/week?${q.toString()}`);
    },
  });

  const dettaglio = useQuery({
    queryKey: [CHIAVE, 'prova', tipo, campoId, durata, giocatori, cella?.date, cella?.time],
    enabled: cella !== null,
    queryFn: () => {
      const q = new URLSearchParams(parametri);
      q.set('date', cella!.date);
      q.set('time', cella!.time);
      return chiama<EsitoPrezzo>('GET', `/settings/prices/try?${q.toString()}`);
    },
  });

  return (
    <div className="riquadro prova-prezzo">
      <h3>La settimana dei prezzi</h3>
      <p className="tenue piccolo">
        Ogni mezz'ora, dall'apertura alla chiusura. <strong>Clicca una casella</strong> per sapere quale riga
        decide quel prezzo, e perché.
      </p>

      <div className="riga-campi">
        <label className="campo">
          <span>Tipo</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoPrenotazione)}>
            {TIPI_PRENOTAZIONE.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span>Campo</span>
          <select value={campoId} onChange={(e) => setCampoId(e.target.value)}>
            <option value={OGNI_CAMPO}>— qualunque —</option>
            {campi.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        {/*
          ⚠️ La DURATA non è un dettaglio della prova: il listino la cerca
          IDENTICA (`durationMinutes !== d.durationMinutes` ⇒ la riga non si
          applica). Un circolo con un listino da 90 minuti, chiesto per 60, non
          ha nessun prezzo — e la griglia lo fa vedere invece di lasciarlo
          scoprire alla cassa.
        */}
        <Campo
          etichetta="Durata (min)"
          type="number"
          min={5}
          step={5}
          value={durata}
          onChange={(e) => setDurata(e.target.value)}
        />
        <Campo
          etichetta="In campo"
          type="number"
          min={1}
          max={20}
          value={giocatori}
          onChange={(e) => setGiocatori(e.target.value)}
        />
      </div>

      <div className="scelta-settimana">
        <button type="button" className="piatto" onClick={() => setGiorno((g) => piuGiorni(g, -7))}>
          ‹ settimana prima
        </button>
        <CampoData etichetta="Settimana del" value={giorno} onChange={setGiorno} />
        <button type="button" className="piatto" onClick={() => setGiorno((g) => piuGiorni(g, 7))}>
          settimana dopo ›
        </button>
      </div>
      {/*
        ⭐⭐ La settimana è una DATA VERA, e non «la settimana tipo»: i periodi di
        validità sono lo strato che governa tutto il resto — estate e inverno
        sono due griglie complete — quindi una griglia senza data non potrebbe
        rispondere affatto. Cambiando settimana si vede il listino stagionale
        cambiare sotto gli occhi, che è la sola prova che i periodi funzionano.
      */}
      <p className="tenue piccolo">
        I prezzi di un <strong>periodo</strong> (estate, inverno) si vedono solo nella settimana in cui
        valgono: cambia settimana e la griglia cambia con loro.
      </p>

      {settimana.isLoading && <Attesa cosa="la settimana" />}
      <Guasto errore={settimana.error} />
      {settimana.data && (
        <Griglia
          settimana={settimana.data}
          cella={cella}
          scegli={(date, time) =>
            setCella((c) => (c?.date === date && c?.time === time ? null : { date, time }))
          }
        />
      )}

      {cella && (
        <>
          <Guasto errore={dettaglio.error} />
          {dettaglio.data && <Risposta esito={dettaglio.data} date={cella.date} time={cella.time} />}
        </>
      )}
    </div>
  );
}

/** «1300» → «13» · «1250» → «12,50». Nella griglia il simbolo è nella legenda. */
function soldiCorti(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2).replace('.', ',');
}

/**
 * La griglia vera: i giorni in colonna, le mezz'ore in riga.
 *
 * ⭐⭐ IL COLORE È ORDINALE, non categorico: il prezzo più basso è il verde più
 * pallido, il più alto il più pieno. Una tavolozza a colori diversi direbbe
 * «questi sono prezzi diversi» e tacerebbe su QUALE è più caro — che è la sola
 * cosa che si guarda a colpo d'occhio.
 * ⚠️ E il colore non è l'unica voce: il numero è scritto in ogni casella, e il
 *    buco porta un trattino. Chi non distingue i verdi legge lo stesso.
 */
function Griglia({
  settimana,
  cella,
  scegli,
}: {
  settimana: SettimanaPrezzi;
  cella: { date: string; time: string } | null;
  scegli: (date: string, time: string) => void;
}): React.JSX.Element {
  // I prezzi distinti, dal più basso al più alto: sono la scala del colore.
  const prezzi = [
    ...new Set(
      settimana.giorni.flatMap((g) =>
        g.celle.map((c) => c.priceCents).filter((p): p is number => p !== null),
      ),
    ),
  ].sort((a, b) => a - b);
  const quanti = settimana.giorni.reduce((n, g) => n + g.celle.length, 0);
  const aTesta = settimana.giorni.some((g) => g.celle.some((c) => c.perPlayer));

  function tinta(cents: number): string {
    if (prezzi.length <= 1) return 'color-mix(in srgb, var(--verde) 30%, transparent)';
    const posto = prezzi.indexOf(cents) / (prezzi.length - 1);
    return `color-mix(in srgb, var(--verde) ${Math.round(14 + posto * 44)}%, transparent)`;
  }

  return (
    <>
      {/*
        ⚠️ Il contenitore ha un'ALTEZZA, e non è estetica: con trentadue righe
        l'intestazione dei giorni esce dallo schermo appena si scorre, e a metà
        griglia nessuno sa più se quella colonna è il martedì o il sabato. Un
        `sticky` dentro un contenitore alto quanto la tabella non si attacca a
        niente: per attaccarsi, il contenitore deve essere quello che scorre.
      */}
      <div className="griglia-scorre">
        <table className="griglia-prezzi">
          <thead>
            <tr>
              <th />
              {settimana.giorni.map((g) => (
                <th key={g.date} scope="col">
                  {GIORNI[g.weekday]}
                  <span className="tenue piccolo">{inItaliano(g.date).slice(0, 5)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {settimana.ore.map((ora, i) => (
              <tr key={ora}>
                {/* ⚠️ L'ora piena in grassetto: trenta righe tutte uguali non si
                    percorrono con l'occhio, e il confine delle 19:30 si cerca
                    partendo dalle 19:00. */}
                <th scope="row" className={ora.endsWith(':00') ? 'ora piena' : 'ora'}>
                  {ora}
                </th>
                {settimana.giorni.map((g) => {
                  const c = g.celle[i]!;
                  const scelta = cella?.date === g.date && cella?.time === ora;
                  return (
                    <td key={g.date}>
                      <button
                        type="button"
                        className={[
                          'cella-prezzo',
                          c.priceCents === null ? 'scoperta' : '',
                          c.ambigua ? 'ambigua' : '',
                          scelta ? 'scelta' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={c.priceCents === null ? undefined : { background: tinta(c.priceCents) }}
                        aria-pressed={scelta}
                        onClick={() => scegli(g.date, ora)}
                        title={
                          c.priceCents === null
                            ? `${GIORNI[g.weekday]} ${inItaliano(g.date)} alle ${ora}: nessun prezzo`
                            : `${GIORNI[g.weekday]} ${inItaliano(g.date)} alle ${ora}: ${euro(c.priceCents)} ${
                                c.perPlayer ? `a testa · ${euro(c.courtCents ?? 0)} il campo` : 'il campo'
                              }${c.ambigua ? ' · due righe se lo contendono' : ''}`
                        }
                      >
                        {c.priceCents === null ? '–' : soldiCorti(c.priceCents)}
                        {c.ambigua && <span aria-hidden="true">⚠</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={settimana.scoperte > 0 ? 'guasto' : 'tenue piccolo'}>
        {settimana.scoperte === 0 ? (
          <>Tutta la settimana ha un prezzo.</>
        ) : (
          <>
            <strong>
              {settimana.scoperte} mezz'ore su {quanti} non hanno prezzo
            </strong>
            {' — '}
            una prenotazione che nasce lì dentro non costa «zero»: costa <em>niente che sappiamo</em>, e lo
            scopre la cassa.
          </>
        )}
        {settimana.ambigue > 0 && ` · ${settimana.ambigue} sono contese da due righe.`}
      </p>

      {prezzi.length > 0 && (
        <p className="legenda-prezzi">
          {prezzi.map((p) => (
            <span key={p}>
              <i style={{ background: tinta(p) }} aria-hidden="true" />
              {euro(p)}
            </span>
          ))}
          <span className="tenue piccolo">{aTesta ? 'a testa' : 'il campo'}</span>
        </p>
      )}
    </>
  );
}

/** ⚠️ Il punto in cui il listino dà DUE risposte, e nessuno lo cerca da sé. */
function Conflitti({ conflitti }: { conflitti: Conflitto[] }): React.JSX.Element {
  return (
    <div className="riquadro avviso" role="alert">
      <h3>
        {conflitti.length === 1 ? 'Una coppia di righe' : `${conflitti.length} coppie di righe`} si contendono
        gli stessi casi
      </h3>
      <p className="tenue piccolo">
        Non è un errore del programma: sono due righe scritte in modo che, per certi casi, la regola non sa
        quale far vincere. Un prezzo viene dato lo stesso — restare senza sarebbe peggio — ma è il punto in
        cui la cassa può non quadrare.
      </p>
      <ul>
        {conflitti.map((c, i) => (
          <li key={i}>
            <code>{descrivi(c.a)}</code> ↔ <code>{descrivi(c.b)}</code>
            <br />
            <span className="tenue piccolo">{c.perche}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function descrivi(r: RigaListino): string {
  const pezzi = [r.bookingType, `${r.durationMinutes}′`];
  if (r.weekday !== null) pezzi.push(GIORNI[r.weekday]!);
  if (r.fromTime) pezzi.push(`${r.fromTime}–${r.toTime}`);
  if (r.courtId) pezzi.push('un campo');
  if (r.validFrom || r.validTo)
    pezzi.push(`${r.validFrom ? inItaliano(r.validFrom) : '…'}→${r.validTo ? inItaliano(r.validTo) : '…'}`);
  pezzi.push(euro(r.priceCents) + (r.perPlayer ? '/testa' : ''));
  return pezzi.join(' · ');
}

function Risposta({
  esito,
  date,
  time,
}: {
  esito: EsitoPrezzo;
  date: string;
  time: string;
}): React.JSX.Element {
  const quando = `${GIORNI[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? ''} ${inItaliano(date)}, ore ${time}`;

  // ⛔ Nessun prezzo NON è zero: «non lo sappiamo» e «gratis» sono due cose
  // diverse, e una partita segnata gratis non la rivede più nessuno.
  if (esito.prezzoCampoCents === null) {
    return (
      <div className="esito nessuno">
        <strong>Nessun prezzo</strong>
        <p className="tenue piccolo">
          {quando}. {esito.perche}
        </p>
      </div>
    );
  }

  return (
    <div className={`esito ${esito.ambigua ? 'ambiguo' : 'buono'}`}>
      <strong className="numerone">{euro(esito.prezzoCampoCents)}</strong>
      <span className="tenue">il campo · {esito.quoteGiocatoriCents.map(euro).join(' + ')} a testa</span>
      <p className="piccolo">
        {quando}. {esito.perche}
      </p>

      {esito.ambigua && (
        <p className="piccolo" role="alert">
          ⚠️ {esito.concorrenti.length} righe si contendono il primo posto: separale, o il prezzo dipende da
          come sono ordinate.
        </p>
      )}

      {esito.applicabili.length > 1 && (
        <details>
          <summary className="piccolo">
            {esito.applicabili.length} righe si applicavano — chi ha vinto e chi no
          </summary>
          <ol className="piccolo">
            {esito.applicabili.map((r, i) => (
              <li key={r.id} className={i === 0 ? 'vince' : ''}>
                {descrivi(r)}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

// ── L'elenco, e la modifica di una riga ─────────────────────────────────────

function Riga({
  riga,
  campi,
  modificabile,
  scelta,
  scegli,
}: {
  riga: RigaListino;
  campi: CampoCircolo[];
  modificabile: boolean;
  scelta: boolean;
  scegli: (id: string, dentro: boolean) => void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [aperto, setAperto] = useState(false);
  const [prezzo, setPrezzo] = useState(String(riga.priceCents / 100).replace('.', ','));
  const elimina = useMutation({
    meta: { fatto: 'riga del listino eliminata' },
    mutationFn: () => chiama('DELETE', `/settings/prices/${riga.id}`),
    onSuccess: rileggi,
  });
  const salva = useMutation({
    meta: { fatto: (_d, c) => `prezzo cambiato: ${inEuro(c as number)}` },
    mutationFn: (priceCents: number) => chiama('PATCH', `/settings/prices/${riga.id}`, { priceCents }),
    onSuccess: rileggi,
  });

  return (
    <>
      <tr className={scelta ? 'scelta' : ''}>
        {modificabile && (
          <td>
            <input
              type="checkbox"
              checked={scelta}
              onChange={(e) => scegli(riga.id, e.target.checked)}
              aria-label={`Scegli la riga ${descrivi(riga)}`}
            />
          </td>
        )}
        <td>{riga.bookingType}</td>
        <td>{riga.weekday === null ? 'ogni giorno' : GIORNI[riga.weekday]}</td>
        <td>{riga.fromTime ? `${riga.fromTime}–${riga.toTime}` : 'ogni ora'}</td>
        <td>{riga.courtId ? (campi.find((c) => c.id === riga.courtId)?.name ?? '—') : 'ogni campo'}</td>
        <td>{riga.durationMinutes}′</td>
        <td>
          {riga.validFrom || riga.validTo
            ? `${riga.validFrom ? inItaliano(riga.validFrom) : '…'} → ${riga.validTo ? inItaliano(riga.validTo) : '…'}`
            : 'sempre'}
        </td>
        <td>
          {/*
            ⚠️ La casella del prezzo resta QUI, accanto al numero che si legge:
            è il gesto di tutti i giorni (un prezzo che cambia), e farlo passare
            da un pannello lo allungherebbe per far posto a quelli rari.
            Il pannello «Modifica» serve a cambiare ciò che DEFINISCE la riga.
          */}
          {modificabile ? (
            <span className="prezzo-modifica">
              <input
                inputMode="decimal"
                value={prezzo}
                onChange={(e) => setPrezzo(e.target.value)}
                aria-label="Prezzo in euro"
              />
              <button
                type="button"
                className="piatto"
                disabled={salva.isPending}
                onClick={() => {
                  const c = centesimi(prezzo);
                  if (c !== null) salva.mutate(c);
                }}
              >
                Salva
              </button>
            </span>
          ) : (
            euro(riga.priceCents)
          )}
          <span className="tenue piccolo"> {riga.perPlayer ? 'a testa' : 'il campo'}</span>
        </td>
        <td className="azioni">
          {modificabile && (
            <>
              <button
                type="button"
                className="piatto"
                aria-expanded={aperto}
                onClick={() => setAperto((a) => !a)}
              >
                Modifica
              </button>
              <button
                type="button"
                className="piatto pericolo"
                disabled={elimina.isPending}
                onClick={() => elimina.mutate()}
              >
                Elimina
              </button>
            </>
          )}
          <Guasto errore={salva.error ?? elimina.error} />
        </td>
      </tr>
      {modificabile && aperto && (
        <tr className="riga-modifica">
          <td colSpan={9}>
            <Modifica riga={riga} campi={campi} chiudi={() => setAperto(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Cambiare una riga già scritta — ⭐ **tutta**, non solo il prezzo.
 *
 * 🗣️ Sua richiesta del 17/09/2026: *«soprattutto poter modificare un listino»*.
 * Prima si poteva cambiare solo il prezzo: spostare una riga dal lunedì al
 * martedì voleva dire cancellarla e riscriverla.
 *
 * ⛔ E cancellare-e-rifare non è lo stesso gesto in due tempi: nel mezzo quelle
 * ore restano **senza prezzo**, e una prenotazione che nasce lì dentro non dà
 * nessun errore. È la ragione per cui le Fasce hanno un pannello «Modifica»,
 * moltiplicata per il fatto che qui il buco non si vede.
 *
 * ⭐ Si manda SOLO ciò che è cambiato davvero: uno schema di modifica dice «non
 * toccarlo» per ciò che non arriva, quindi rispedire tutto vorrebbe dire
 * riscrivere anche i campi che nessuno ha guardato.
 */
function Modifica({
  riga,
  campi,
  chiudi,
}: {
  riga: RigaListino;
  campi: CampoCircolo[];
  chiudi: () => void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [tipo, setTipo] = useState<TipoPrenotazione>(riga.bookingType);
  const [giorno, setGiorno] = useState(riga.weekday === null ? '' : String(riga.weekday));
  const [campoId, setCampoId] = useState(riga.courtId ?? OGNI_CAMPO);
  const [dalle, setDalle] = useState(riga.fromTime ?? '');
  const [alle, setAlle] = useState(riga.toTime ?? '');
  const [durata, setDurata] = useState(String(riga.durationMinutes));
  const [prezzo, setPrezzo] = useState(String(riga.priceCents / 100).replace('.', ','));
  const [aTesta, setATesta] = useState(riga.perPlayer);
  const [dal, setDal] = useState(riga.validFrom ?? '');
  const [al, setAl] = useState(riga.validTo ?? '');

  const cambiato: Record<string, unknown> = {};
  if (tipo !== riga.bookingType) cambiato.bookingType = tipo;
  const weekday = giorno === '' ? null : Number(giorno);
  if (weekday !== riga.weekday) cambiato.weekday = weekday;
  const courtId = campoId === OGNI_CAMPO ? null : campoId;
  if (courtId !== riga.courtId) cambiato.courtId = courtId;
  // ⚠️ Le due ore viaggiano INSIEME, sempre: mezza fascia il server la rifiuta,
  //    e ha ragione — una riga con un solo estremo non la sa applicare nessuno.
  const daOra = dalle === '' ? null : dalle;
  const aOra = alle === '' ? null : alle;
  if (daOra !== riga.fromTime || aOra !== riga.toTime) {
    cambiato.fromTime = daOra;
    cambiato.toTime = aOra;
  }
  if (Number(durata) !== riga.durationMinutes) cambiato.durationMinutes = Number(durata);
  const cents = centesimi(prezzo);
  if (cents !== null && cents !== riga.priceCents) cambiato.priceCents = cents;
  if (aTesta !== riga.perPlayer) cambiato.perPlayer = aTesta;
  const validFrom = dal === '' ? null : dal;
  if (validFrom !== riga.validFrom) cambiato.validFrom = validFrom;
  const validTo = al === '' ? null : al;
  if (validTo !== riga.validTo) cambiato.validTo = validTo;

  const salva = useMutation({
    meta: { fatto: 'riga del listino salvata' },
    mutationFn: () => chiama('PATCH', `/settings/prices/${riga.id}`, cambiato),
    onSuccess: () => {
      rileggi();
      chiudi();
    },
  });

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (Object.keys(cambiato).length > 0) salva.mutate();
        else chiudi();
      }}
    >
      <div className="riga-campi">
        <label className="campo">
          <span>Tipo</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoPrenotazione)}>
            {TIPI_PRENOTAZIONE.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span>Giorno</span>
          <select value={giorno} onChange={(e) => setGiorno(e.target.value)}>
            <option value="">ogni giorno</option>
            {ORDINE_SETTIMANA.map((i) => (
              <option key={i} value={i}>
                {GIORNI[i]}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span>Campo</span>
          <select value={campoId} onChange={(e) => setCampoId(e.target.value)}>
            <option value={OGNI_CAMPO}>ogni campo</option>
            {campi.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.active ? '' : ' (spento)'}
              </option>
            ))}
          </select>
        </label>
        <CampoOra etichetta="Dalle" value={dalle} onChange={setDalle} />
        <CampoOra etichetta="Alle" value={alle} onChange={setAlle} />
        <Campo
          etichetta="Durata (min)"
          type="number"
          min={5}
          step={5}
          value={durata}
          onChange={(e) => setDurata(e.target.value)}
        />
        <Campo
          etichetta="Prezzo (€)"
          inputMode="decimal"
          value={prezzo}
          onChange={(e) => setPrezzo(e.target.value)}
        />
        <CampoData etichetta="Vale dal" value={dal} onChange={setDal} />
        <CampoData etichetta="Vale al" value={al} onChange={setAl} />
      </div>
      <label className="campo orizzontale">
        <input type="checkbox" checked={aTesta} onChange={(e) => setATesta(e.target.checked)} />
        <span>Il prezzo è a giocatore</span>
      </label>
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending}>
          {salva.isPending ? 'Un attimo…' : 'Salva la riga'}
        </button>
        <button type="button" className="piatto" onClick={chiudi}>
          Lascia com'era
        </button>
        <span className="tenue piccolo">
          {Object.keys(cambiato).length === 0
            ? 'Niente da cambiare.'
            : `${Object.keys(cambiato).length} cose cambiate.`}
        </span>
      </div>
    </form>
  );
}

/** «12,50» → 1250. `null` su ciò che non è un importo: mai un `NaN` in giro. */
function centesimi(testo: string): number | null {
  const pulito = testo.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(pulito)) return null;
  return Math.round(Number(pulito) * 100);
}

// ── ⭐⭐ Ripetere: copiare le righe scelte su altri giorni ────────────────────

/**
 * 🗣️ Sua richiesta del 17/09/2026: *«ripeterlo per alcuni giorni e soprattutto
 * per alcune fasce orarie in determinati giorni della settimana»*.
 *
 * ⭐ È la barra delle Fasce, con UNA differenza che cambia il disegno e va
 * detta: là a impedire un doppione c'è il DATABASE (le fasce hanno un vincolo
 * di non sovrapposizione), qui **non c'è nessun vincolo** — due righe che si
 * contendono lo stesso caso si scrivono benissimo, e il listino comincia a dare
 * due risposte senza che niente si lamenti.
 * ⇒ Per questo la copia dice PRIMA di scrivere quante contese creerebbe: è
 *   l'unico «no» che esiste da queste parti, e deve arrivare prima del gesto.
 */
function BarraScelte({
  scelte,
  tutte,
  campi,
  svuota,
}: {
  scelte: RigaListino[];
  tutte: RigaListino[];
  campi: CampoCircolo[];
  svuota: () => void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [giorni, setGiorni] = useState<number[]>([]);
  const [prezzo, setPrezzo] = useState('');

  /** Le righe che nascerebbero: servono al conto delle contese, prima di scrivere. */
  const candidate: RigaListino[] = giorni.flatMap((g) =>
    scelte.filter((r) => r.weekday !== g).map((r) => ({ ...r, id: `${r.id}-${g}`, weekday: g })),
  );
  const idCandidate = new Set(candidate.map((r) => r.id));
  const contese = trovaConflitti([...tutte, ...candidate]).filter(
    (c) => idCandidate.has(c.a.id) || idCandidate.has(c.b.id),
  ).length;

  const copia = useMutation({
    meta: { fatto: (d) => perGiorni(d as Esito[], 'riga copiata') },
    mutationFn: async (): Promise<Esito[]> => {
      const esiti: Esito[] = [];
      // Una per volta e non in parallelo: in fila, l'errore che torna resta
      // attaccato alla sua coppia senza doverlo ricostruire.
      for (const g of giorni) {
        for (const r of scelte) {
          // ⚠️ Una riga non si copia sul PROPRIO giorno: sarebbe il doppione
          //    perfetto, cioè due righe identiche che si contendono tutto.
          if (r.weekday === g) continue;
          try {
            await chiama('POST', '/settings/prices', {
              bookingType: r.bookingType,
              courtId: r.courtId,
              weekday: g,
              fromTime: r.fromTime,
              toTime: r.toTime,
              durationMinutes: r.durationMinutes,
              priceCents: r.priceCents,
              perPlayer: r.perPlayer,
              validFrom: r.validFrom,
              validTo: r.validTo,
            });
            esiti.push({ giorno: g, riga: r, errore: null });
          } catch (e) {
            esiti.push({ giorno: g, riga: r, errore: (e as Error).message });
          }
        }
      }
      return esiti;
    },
    onSuccess: (esiti) => {
      rileggi();
      // Restano scelti solo i giorni che hanno avuto almeno un rifiuto: un
      // secondo giro su un giorno riuscito scriverebbe dei doppioni.
      setGiorni((p) => p.filter((g) => esiti.some((e) => e.giorno === g && e.errore !== null)));
    },
  });

  /**
   * ⭐ Cambiare il prezzo a TUTTE le righe scelte.
   *
   * ⚖️ È l'obiezione seria alla scelta di tenere una riga per giorno invece di
   * una riga con dentro un insieme di giorni: «alzo la sera di un euro» sarebbe
   * cinque modifiche. Con una spunta e una casella torna a essere un gesto solo
   * — e la funzione che sceglie il prezzo, che è la più pericolosa del
   * progetto, non è stata toccata.
   */
  const cambiaPrezzo = useMutation({
    meta: {
      fatto: (_d, c) =>
        `prezzo cambiato su ${scelte.length === 1 ? '1 riga' : `${scelte.length} righe`}: ${inEuro(c as number)}`,
    },
    mutationFn: async (cents: number) => {
      for (const r of scelte) await chiama('PATCH', `/settings/prices/${r.id}`, { priceCents: cents });
    },
    onSuccess: () => {
      setPrezzo('');
      rileggi();
    },
  });

  const esiti = copia.data ?? [];
  const perGiorno = [...new Set(esiti.map((e) => e.giorno))].sort(
    (a, b) => ORDINE_SETTIMANA.indexOf(a as 0) - ORDINE_SETTIMANA.indexOf(b as 0),
  );
  const cents = centesimi(prezzo);

  return (
    <div className="barra-scelta">
      <div className="barra-scelta-testa">
        <strong>
          {scelte.length} {scelte.length === 1 ? 'riga scelta' : 'righe scelte'}
        </strong>
        <span className="tenue piccolo">
          {scelte
            .slice(0, 4)
            .map((r) => descrivi(r))
            .join(' · ')}
          {scelte.length > 4 && ` · e altre ${scelte.length - 4}`}
        </span>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (giorni.length > 0) copia.mutate();
        }}
      >
        <p className="tenue piccolo">Copiale su altri giorni della settimana:</p>
        <div className="giorni-scelta">
          {ORDINE_SETTIMANA.map((i) => (
            <label key={i} className="campo orizzontale">
              <input
                type="checkbox"
                disabled={copia.isPending}
                checked={giorni.includes(i)}
                onChange={(e) => setGiorni((p) => (e.target.checked ? [...p, i] : p.filter((g) => g !== i)))}
              />
              <span>{GIORNI[i]}</span>
            </label>
          ))}
        </div>

        {/*
          ⚠️ L'avviso arriva PRIMA della scrittura, non dopo: dopo sarebbe un
          elenco di errori da rileggere; prima è una scelta.
        */}
        {contese > 0 && (
          <p className="guasto" role="alert">
            Questa copia creerebbe <strong>{contese}</strong>{' '}
            {contese === 1
              ? 'punto in cui il listino dà due risposte'
              : 'punti in cui il listino dà due risposte'}
            : su quei giorni c'è già una riga che copre gli stessi casi. Qui nessun vincolo la fermerà — il
            prezzo dipenderebbe da come le righe sono ordinate.
          </p>
        )}

        {perGiorno.map((g) => {
          const suoi = esiti.filter((e) => e.giorno === g);
          const ok = suoi.filter((e) => e.errore === null);
          const no = suoi.filter((e) => e.errore !== null);
          return (
            <p
              key={g}
              className={no.length > 0 ? 'guasto' : 'tenue piccolo'}
              role={no.length > 0 ? 'alert' : undefined}
            >
              <strong>{GIORNI[g]}</strong>: {ok.length} {ok.length === 1 ? 'copiata' : 'copiate'}
              {no.length > 0 && (
                <>
                  , {no.length} no — {no.map((e) => e.errore).join(' · ')}
                </>
              )}
            </p>
          );
        })}
        {copia.error ? <Guasto errore={copia.error} /> : null}

        <div className="azioni">
          <button type="submit" disabled={copia.isPending || giorni.length === 0}>
            {copia.isPending
              ? 'Un attimo…'
              : `Copia su ${giorni.length} ${giorni.length === 1 ? 'giorno' : 'giorni'}`}
          </button>
        </div>
      </form>

      <form
        className="cambio-prezzo"
        onSubmit={(e) => {
          e.preventDefault();
          if (cents !== null) cambiaPrezzo.mutate(cents);
        }}
      >
        <label className="campo">
          <span>Nuovo prezzo (€) per tutte le righe scelte</span>
          <input inputMode="decimal" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} />
        </label>
        <button type="submit" disabled={cambiaPrezzo.isPending || cents === null}>
          {cambiaPrezzo.isPending ? 'Un attimo…' : `Cambia il prezzo a ${scelte.length}`}
        </button>
        <Guasto errore={cambiaPrezzo.error} />
      </form>

      <div className="azioni">
        <button type="button" className="piatto" onClick={svuota}>
          Togli la selezione
        </button>
        <span className="tenue piccolo">
          {campi.length} {campi.length === 1 ? 'campo' : 'campi'} in tutto: le copie restano sul campo della
          riga di partenza.
        </span>
      </div>
    </div>
  );
}

/** L'esito di UNA copia: quale riga, su quale giorno, e com'è andata. */
interface Esito {
  giorno: number;
  riga: RigaListino;
  errore: string | null;
}

// ── ⭐ La riga nuova: un gesto, tanti giorni ─────────────────────────────────

/**
 * 🗣️ *«semplificarmi la creazione dei listini»* ⇒ il menu «un giorno solo» è
 * diventato una fila di giorni da accendere. Il prezzo della sera su lunedì,
 * mercoledì e venerdì era tre giri identici di questo stesso modulo.
 *
 * ⚖️ Restano **righe separate**, una per giorno, e non una riga con dentro un
 * insieme di giorni. Non è pigrizia: la funzione che sceglie il prezzo è quella
 * che il piano chiama la più pericolosa del progetto, e una riga «da lunedì a
 * venerdì» le cambierebbe la graduatoria — quanto vale «lun-ven» contro «solo
 * lunedì»? — in un punto dove l'errore non dà nessun errore. ⇒ Si cambia il
 * GESTO, che costa poco e non può mentire, e non la regola.
 * 📌 E la strada resta aperta: da righe separate si può sempre passare a righe
 *    con più giorni; dall'altra parte no, ci vorrebbe una migrazione.
 */
function Nuova({ campi }: { campi: CampoCircolo[] }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [tipo, setTipo] = useState<TipoPrenotazione>('partita');
  const [giorni, setGiorni] = useState<number[]>([]);
  const [dalle, setDalle] = useState('');
  const [alle, setAlle] = useState('');
  const [campoId, setCampoId] = useState(OGNI_CAMPO);
  const [durata, setDurata] = useState('90');
  const [prezzo, setPrezzo] = useState('');
  const [aTesta, setATesta] = useState(true);
  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');

  const cents = centesimi(prezzo);

  const crea = useMutation({
    meta: {
      fatto: (d) =>
        (d as Esito[]).length === 0
          ? 'riga del listino aggiunta, vale ogni giorno'
          : perGiorni(d as Esito[], 'riga del listino aggiunta'),
    },
    mutationFn: async (): Promise<Esito[]> => {
      const corpo = {
        bookingType: tipo,
        courtId: campoId === OGNI_CAMPO ? null : campoId,
        fromTime: dalle === '' ? null : dalle,
        toTime: alle === '' ? null : alle,
        durationMinutes: Number(durata),
        priceCents: cents ?? -1,
        perPlayer: aTesta,
        validFrom: dal === '' ? null : dal,
        validTo: al === '' ? null : al,
      };
      // ⚠️ Nessun giorno acceso = UNA riga che vale ogni giorno. Non è lo stesso
      //    che accenderli tutti e sette: quella sarebbe sette righe, e ognuna
      //    più specifica della generale — cioè un listino diverso.
      if (giorni.length === 0) {
        await chiama('POST', '/settings/prices', { ...corpo, weekday: null });
        return [];
      }
      const esiti: Esito[] = [];
      for (const g of giorni) {
        try {
          const r = await chiama<RigaListino>('POST', '/settings/prices', { ...corpo, weekday: g });
          esiti.push({ giorno: g, riga: r, errore: null });
        } catch (e) {
          esiti.push({ giorno: g, riga: null as unknown as RigaListino, errore: (e as Error).message });
        }
      }
      return esiti;
    },
    onSuccess: (esiti) => {
      if (esiti.every((e) => e.errore === null)) setPrezzo('');
      rileggi();
    },
  });

  const falliti = (crea.data ?? []).filter((e) => e.errore !== null);
  const riusciti = (crea.data ?? []).filter((e) => e.errore === null);

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Aggiungi un prezzo</h3>
      <p className="tenue piccolo">
        Lasciare un campo vuoto vuol dire «vale sempre». Una riga più specifica <em>aggiunge</em> un criterio
        a una più generale: se ne scambia uno, le due si contendono gli stessi casi.
      </p>
      <div className="riga-campi">
        <label className="campo">
          <span>Tipo</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoPrenotazione)}>
            {TIPI_PRENOTAZIONE.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span>Campo</span>
          <select value={campoId} onChange={(e) => setCampoId(e.target.value)}>
            <option value={OGNI_CAMPO}>ogni campo</option>
            {campi.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <CampoOra etichetta="Dalle" value={dalle} onChange={setDalle} />
        <CampoOra etichetta="Alle" value={alle} onChange={setAlle} />
        <Campo
          etichetta="Durata (min)"
          type="number"
          min={5}
          step={5}
          required
          value={durata}
          onChange={(e) => setDurata(e.target.value)}
        />
        <Campo
          etichetta="Prezzo (€)"
          inputMode="decimal"
          required
          value={prezzo}
          onChange={(e) => setPrezzo(e.target.value)}
        />
        <CampoData etichetta="Vale dal" value={dal} onChange={setDal} />
        <CampoData etichetta="Vale al" value={al} onChange={setAl} />
      </div>

      <div className="campo">
        <span>Giorni della settimana</span>
        <div className="giorni-settimana">
          {ORDINE_SETTIMANA.map((i) => (
            <button
              key={i}
              type="button"
              className={giorni.includes(i) ? 'giorno acceso' : 'giorno'}
              aria-pressed={giorni.includes(i)}
              onClick={() => setGiorni((p) => (p.includes(i) ? p.filter((g) => g !== i) : [...p, i]))}
            >
              {GIORNI[i]!.slice(0, 3)}
            </button>
          ))}
        </div>
        <span className="tenue piccolo">
          {giorni.length === 0
            ? 'Nessuno acceso: nasce una riga sola, che vale ogni giorno.'
            : `Nascono ${giorni.length} righe, una per giorno acceso — così ognuna si cambia da sé.`}
        </span>
      </div>

      <label className="campo orizzontale">
        <input type="checkbox" checked={aTesta} onChange={(e) => setATesta(e.target.checked)} />
        <span>Il prezzo è a giocatore (come il listino del circolo)</span>
      </label>
      <Guasto errore={crea.error} />
      {riusciti.length > 0 && falliti.length === 0 && (
        <p className="tenue piccolo">
          {riusciti.length} {riusciti.length === 1 ? 'riga scritta' : 'righe scritte'}:{' '}
          {riusciti.map((e) => GIORNI[e.giorno]).join(' · ')}.
        </p>
      )}
      {falliti.length > 0 && (
        <p className="guasto" role="alert">
          {falliti.map((e) => `${GIORNI[e.giorno]}: ${e.errore}`).join(' · ')}
        </p>
      )}
      <button type="submit" disabled={crea.isPending}>
        {crea.isPending
          ? 'Un attimo…'
          : giorni.length > 1
            ? `Aggiungi su ${giorni.length} giorni`
            : 'Aggiungi'}
      </button>
    </form>
  );
}
